import { IonContent, IonRange } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { layoutDataDefault } from '../../constants';
import { LayoutDataType } from '../../types';

import { PersonalityStyles } from './Personality.styles';

export const ScreenPersonality: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Personality',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();
  const personality = form.watch('personality');

  const handleNextStep = (): void => {
    userService.createOrUpdate({
      onboarding_data: {
        personality,
      },
    });
    wizard.nextStep();
  };

  return (
    <PersonalityStyles background={'purple-gradient'}>
      <OnboardingNavigation />

      <OwnAiTitle className="personality-title">
        Dial in His Personality
      </OwnAiTitle>

      <IonContent>
        <div>
          <div className="range-labels">
            <UiTypography variant="label-large">😈 Dominant</UiTypography>
            <UiTypography variant="label-large">Submissive 🤐</UiTypography>
          </div>
          <IonRange
            value={personality.dominant_submissive}
            ticks={false}
            defaultValue={layoutDataDefault.personality.dominant_submissive}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.dominant_submissive',
                detail.value as number,
              );
            }}
          />
        </div>

        <div>
          <div className="range-labels">
            <UiTypography variant="label-large">🤪 Goofy</UiTypography>
            <UiTypography variant="label-large">Serious 🗿</UiTypography>
          </div>
          <IonRange
            value={personality.goofy_serious}
            ticks={false}
            defaultValue={layoutDataDefault.personality.goofy_serious}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.goofy_serious',
                detail.value as number,
              );
            }}
          />
        </div>

        <div>
          <div className="range-labels">
            <UiTypography variant="label-large">💥 Chaotic</UiTypography>
            <UiTypography variant="label-large">Thoughtful 🧠</UiTypography>
          </div>
          <IonRange
            value={personality.chaotic_thoughtful}
            ticks={false}
            defaultValue={layoutDataDefault.personality.chaotic_thoughtful}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.chaotic_thoughtful',
                detail.value as number,
              );
            }}
          />
        </div>

        <div>
          <div className="range-labels">
            <UiTypography variant="label-large">🔍 Curious</UiTypography>
            <UiTypography variant="label-large">Certain 🎯</UiTypography>
          </div>
          <IonRange
            value={personality.curious_certain}
            ticks={false}
            defaultValue={layoutDataDefault.personality.curious_certain}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.curious_certain',
                detail.value as number,
              );
            }}
          />
        </div>
      </IonContent>
      <OnboardingFooter
        onContinue={handleNextStep}
        buttonColor={'purple-gradient'}
        buttonRadius={'sm'}
        buttonSize={'large'}
        buttonClassName={'page-footer'}
      />
    </PersonalityStyles>
  );
};
